var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Big from "big.js";
import { parseEscrowLockedBalance } from "../types";
import { ATOMIC_UNIT, MS_PER_YEAR, decodeFixedPointType, newCurrencyId, newMonetaryAmount, toVoting } from "../utils";
export class DefaultEscrowAPI {
    constructor(api, governanceCurrency, systemApi) {
        this.api = api;
        this.governanceCurrency = governanceCurrency;
        this.systemApi = systemApi;
    }
    createLock(amount, unlockHeight) {
        const tx = this.api.tx.escrow.createLock(amount.toString(true), unlockHeight);
        return { extrinsic: tx, event: this.api.events.escrow.Deposit };
    }
    withdraw() {
        const tx = this.api.tx.escrow.withdraw();
        return { extrinsic: tx, event: this.api.events.escrow.Withdraw };
    }
    withdrawRewards() {
        const tx = this.api.tx.escrowAnnuity.withdrawRewards();
        return { extrinsic: tx, event: this.api.events.escrowRewards.WithdrawReward };
    }
    increaseAmount(amount) {
        const tx = this.api.tx.escrow.increaseAmount(amount.toString(true));
        return { extrinsic: tx, event: this.api.events.escrow.Deposit };
    }
    increaseUnlockHeight(unlockHeight) {
        const tx = this.api.tx.escrow.increaseUnlockHeight(unlockHeight);
        return { extrinsic: tx, event: this.api.events.escrow.Deposit };
    }
    getRewards(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const reward = yield this.api.rpc.reward.computeEscrowReward(accountId, newCurrencyId(this.api, this.governanceCurrency));
            return newMonetaryAmount(reward.amount.toString(), this.governanceCurrency);
        });
    }
    getRewardEstimate(accountId, amountToLock, newLockEndHeight) {
        return __awaiter(this, void 0, void 0, function* () {
            const stakedBalance = yield this.getStakedBalance(accountId);
            let atomicStakedAmount = stakedBalance.amount.toBig(ATOMIC_UNIT);
            const currentLockEndHeight = stakedBalance.endBlock;
            let nullableAmountToCheck = null;
            if (amountToLock && !amountToLock.isZero()) {
                atomicStakedAmount = atomicStakedAmount.add(amountToLock.toBig(ATOMIC_UNIT));
                // get atomic amount to lock as bigint for the rpc api
                nullableAmountToCheck = BigInt(amountToLock.toBig(ATOMIC_UNIT).toString());
            }
            let nullableBlockToEnd = null;
            if (newLockEndHeight) {
                nullableBlockToEnd = BigInt(newLockEndHeight);
            }
            // if there is no staked amount and no added amount to be checked, return 0
            if (atomicStakedAmount.eq(Big(0))) {
                return {
                    amount: newMonetaryAmount(0, this.governanceCurrency),
                    apy: Big(0),
                };
            }
            const rawRewardRate = yield this.api.rpc.reward.estimateEscrowRewardRate(accountId, nullableAmountToCheck, nullableBlockToEnd);
            // annual reward rate
            const rewardRate = decodeFixedPointType(rawRewardRate);
            // reward amount, annualized
            const rewardAmountAnnualized = newMonetaryAmount(atomicStakedAmount, this.governanceCurrency).mul(rewardRate);
            const effectiveLockEndHeight = newLockEndHeight || currentLockEndHeight;
            const [blockTimeMs, currentHeight] = yield Promise.all([
                this.api.call.auraApi.slotDuration().then((u) => u.toBigInt()),
                this.systemApi.getCurrentBlockNumber(),
            ]);
            const blocksPerYear = MS_PER_YEAR.div(blockTimeMs.toString());
            const rewardAmountTotal = rewardAmountAnnualized.mul(effectiveLockEndHeight - currentHeight).div(blocksPerYear);
            return {
                amount: rewardAmountTotal,
                apy: rewardRate.mul(100),
            };
        });
    }
    getEscrowStake(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawStake = yield this.api.query.escrowRewards.stake([null, accountId]);
            return decodeFixedPointType(rawStake);
        });
    }
    getEscrowTotalStake() {
        return __awaiter(this, void 0, void 0, function* () {
            const rawTotalStake = yield this.api.query.escrowRewards.totalStake(null);
            return decodeFixedPointType(rawTotalStake);
        });
    }
    getRewardTally(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const governanceCurrencyId = newCurrencyId(this.api, this.governanceCurrency);
            const rawRewardTally = yield this.api.query.escrowRewards.rewardTally(governanceCurrencyId, [null, accountId]);
            return decodeFixedPointType(rawRewardTally);
        });
    }
    getRewardPerToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const governanceCurrencyId = newCurrencyId(this.api, this.governanceCurrency);
            const rawRewardPerToken = yield this.api.query.escrowRewards.rewardPerToken(governanceCurrencyId, null);
            return decodeFixedPointType(rawRewardPerToken);
        });
    }
    getRewardPerBlock() {
        return __awaiter(this, void 0, void 0, function* () {
            const rawRewardPerBlock = yield this.api.query.escrowAnnuity.rewardPerBlock();
            return newMonetaryAmount(rawRewardPerBlock.toString(), this.governanceCurrency);
        });
    }
    getStakedBalance(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawStakedBalance = yield this.api.query.escrow.locked(accountId);
            return parseEscrowLockedBalance(this.governanceCurrency, rawStakedBalance);
        });
    }
    getTotalStakedBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const govCcy = this.governanceCurrency;
            const rawStakedBalances = yield this.api.query.escrow.locked.entries();
            const totalCurrentAmount = rawStakedBalances
                .map(([_, rawStakedBalance]) => parseEscrowLockedBalance(govCcy, rawStakedBalance).amount)
                .reduce((acc, amount) => acc.add(amount), newMonetaryAmount(0, govCcy));
            return totalCurrentAmount;
        });
    }
    votingBalance(accountId, blockNumber) {
        return __awaiter(this, void 0, void 0, function* () {
            const maybeBlockNumber = blockNumber === undefined ? null : blockNumber.toString();
            const balance = yield this.api.rpc.escrow.balanceAt(accountId.toString(), maybeBlockNumber);
            return newMonetaryAmount(balance.amount.toString(), toVoting(this.governanceCurrency));
        });
    }
    totalVotingSupply(blockNumber) {
        return __awaiter(this, void 0, void 0, function* () {
            const maybeBlockNumber = blockNumber === undefined ? null : blockNumber.toString();
            const supply = yield this.api.rpc.escrow.totalSupply(maybeBlockNumber);
            return newMonetaryAmount(supply.amount.toString(), toVoting(this.governanceCurrency));
        });
    }
    getSpan() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.api.consts.escrow.span.toBn();
        });
    }
    getMaxPeriod() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.api.consts.escrow.maxPeriod.toBn();
        });
    }
}
//# sourceMappingURL=escrow.js.map